import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { KeyboardEvent } from 'react';
import Autocomplete from 'react-google-autocomplete';
import { useOrganization } from '@clerk/clerk-react';

import { Input } from '@/components/ui/input';
import { Card, CardTitle, CardContent } from '@/components/ui/card';
import { MultiSelector } from '@/routes/settings/components/multi-selector';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { gql, useMutation, useQuery } from '@apollo/client';
import {
  CurrentMerchantForCompanyInformationQuery,
  UpdateMerchantForCompanyInformationMutation
} from '@/gql/graphql';
import { COUNTRIES, MERCHANT_CATEGORIES, TIMEZONES } from '@/constants';
import { formatTimezone } from '@/utils/format-utils';

interface AddressComponent {
  long_name: string;
  short_name: string;
  types: string[];
}

interface GoogleMapsAPIPlace {
  address_components: AddressComponent[];
}

const formSchema = z.object({
  id: z.string().min(1, 'ID is required.'),
  websiteName: z.string().min(1, 'Website Name is required.'),
  merchantCategory: z.string().min(1, 'Merchant Category is required.'),
  websiteUrl: z
    .string()
    .min(1, 'Website is required.')
    .regex(
      /^(https?:\/\/)?(www\.)?[\da-z]+([\-\.]{1}[\da-z]+)*\.[a-z]{2,63}$/i,
      'Please enter a valid website.'
    ),
  businessName: z.string().min(1, 'Legal Business Name is required.'),
  einNumber: z
    .string()
    .min(1, 'EIN Number is required.')
    .regex(/^\d{2}-\d{7}$/, 'Please enter a valid EIN.'),
  address1: z.string().min(1, 'Street Address is required.'),
  address2: z.string().optional(),
  city: z.string().min(1, 'City is required.'),
  state: z.string().min(1, 'State is required.'),
  timezone: z.string().min(1, 'Timezone is required.'),
  zip: z
    .string()
    .min(1, 'Zip Code is required.')
    .regex(/^\d{5}(-\d{4})?$/, 'Please enter a valid ZIP code.'),
  country: z.string().min(1, 'Country is required.')
});

export const CURRENT_MERCHANT_QUERY = gql`
  query CurrentMerchantForCompanyInformation {
    currentMerchant {
      id
      address1
      address2
      businessName
      city
      country
      einNumber
      websiteUrl
      websiteName
      province
      zip
      timezone
      merchantCategory
    }
  }
`;

const UPDATE_MERCHANT_MUTATION = gql`
  mutation UpdateMerchantForCompanyInformation($input: UpdateCurrentMerchant!) {
    updateCurrentMerchant(input: $input) {
      id
      address1
      address2
      businessName
      city
      country
      einNumber
      websiteUrl
      websiteName
      province
      zip
      timezone
      merchantCategory
    }
  }
`;

export function CompanyInformationForm() {
  const { toast } = useToast();
  const { organization } = useOrganization();
  const { data } = useQuery<CurrentMerchantForCompanyInformationQuery>(CURRENT_MERCHANT_QUERY);
  const [updateMerchant, { loading }] =
    useMutation<UpdateMerchantForCompanyInformationMutation>(UPDATE_MERCHANT_MUTATION);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    values: {
      id: data?.currentMerchant.id ?? '',
      websiteName: organization?.name ?? '',
      websiteUrl: data?.currentMerchant.websiteUrl ?? '',
      businessName: data?.currentMerchant.businessName ?? '',
      merchantCategory: data?.currentMerchant.merchantCategory ?? '',
      einNumber: data?.currentMerchant.einNumber ?? '',
      address1: data?.currentMerchant.address1 ?? '',
      address2: data?.currentMerchant.address2 ?? '',
      city: data?.currentMerchant.city ?? '',
      state: data?.currentMerchant.province ?? '',
      timezone: data?.currentMerchant.timezone ?? '',
      zip: data?.currentMerchant.zip ?? '',
      country: data?.currentMerchant.country ?? ''
    },
    mode: 'onChange'
  });

  const { isDirty } = form.formState;

  const formatEIN = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 2) return numbers;
    return `${numbers.slice(0, 2)}-${numbers.slice(2, 9)}`;
  };

  const handlePlaceSelect = (place: GoogleMapsAPIPlace) => {
    const getComponent = (componentType: string, nameType: keyof AddressComponent): string =>
      place.address_components
        .find((component: AddressComponent) => component.types.includes(componentType))
        ?.[nameType].toString() ?? '';

    const streetNumber = getComponent('street_number', 'long_name');
    const route = getComponent('route', 'long_name');
    const address1 = `${streetNumber} ${route}`.trim();
    const stateAbbreviation = getComponent('administrative_area_level_1', 'short_name');

    form.setValue('address1', address1);
    form.setValue('zip', getComponent('postal_code', 'long_name'));
    form.setValue('city', getComponent('locality', 'long_name'));
    form.setValue('state', stateAbbreviation);
    form.setValue('country', getComponent('country', 'short_name'));
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLFormElement | HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  const onSubmit = async (input: z.infer<typeof formSchema>) => {
    try {
      const formattedInput = {
        ...input,
        province: input.state
      };

      const { state, websiteUrl, ...finalInput } = formattedInput;

      await updateMerchant({
        variables: {
          input: { ...finalInput, id: data?.currentMerchant.id }
        }
      });

      if (organization) {
        await organization.update({ name: input.websiteName });
      }

      toast({
        title: 'Form submitted',
        description: 'Your form has been submitted successfully.',
        variant: 'default'
      });
    } catch (error) {
      console.error('Error in onSubmit:', error);

      toast({
        title: 'Error',
        description: 'Something went wrong. Please try again.',
        variant: 'destructive'
      });
    }
  };

  const onError = () => {
    toast({
      title: 'Error',
      description: 'Please fill out all the required fields.',
      variant: 'destructive'
    });
  };

  return (
    <Form {...form}>
      <form
        onSubmit={(e) => {
          void form.handleSubmit(onSubmit, onError)(e);
        }}
        onKeyDown={handleKeyDown}
        className="space-y-4"
      >
        <CardTitle className="text-lg pt-4 pb-4">Account</CardTitle>
        <Card className="mt-6">
          <CardContent className="md:grid-cols-2 grid-cols-1 grid gap-6 pt-4">
            <div className="md:grid-cols-1 grid-cols-1 grid gap-6">
              <FormField
                control={form.control}
                name="websiteName"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel className="text-foreground">
                      Merchant Display Name <span className="text-red-700">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="e.g. Warby Parker" {...field} />
                    </FormControl>
                    <FormMessage className="text-red-700 text-xs" />
                  </FormItem>
                )}
              />
            </div>
            <div className="md:grid-cols-1 grid-cols-1 grid gap-6">
              <FormField
                control={form.control}
                name="id"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel className="text-foreground">Store ID</FormLabel>
                    <FormControl>
                      <Input readOnly={true} disabled={true} {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
        </Card>
        <CardTitle className="text-lg pt-4 pd-4">Website</CardTitle>
        <Card className="mt-6">
          <CardContent className="md:grid-cols-2 grid-cols-1 grid gap-6 pt-4">
            <div className="md:grid-cols-1 grid-cols-1 grid gap-6">
              <FormField
                control={form.control}
                name="websiteUrl"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel className="text-foreground">
                      Website <span className="text-red-700">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        readOnly={!!field.value}
                        disabled={!!field.value}
                        placeholder="e.g. www.warbyparker.com"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage className="text-red-700 text-xs" />
                  </FormItem>
                )}
              />
            </div>
            <div className="md:grid-cols-1 grid-cols-1 grid gap-6">
              <FormField
                control={form.control}
                name="merchantCategory"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel className="text-foreground">Merchant Category</FormLabel>
                    <MultiSelector
                      className="bg-[hsl(var(--input))] border-[hsl(var(--border))]"
                      value={field.value}
                      onChange={field.onChange}
                      items={MERCHANT_CATEGORIES}
                    />
                    <FormMessage className="text-red-700 text-xs" />
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
        </Card>
        <CardTitle className="text-lg pt-4 pd-4">Business Information</CardTitle>
        <Card className="mt-6">
          <CardContent className="md:grid-cols-1 grid-cols-1 grid gap-6">
            <div className="md:grid-cols-2 grid-cols-1 grid gap-6 pt-4">
              <FormField
                control={form.control}
                name="businessName"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel className="text-foreground">
                      Legal Business Name <span className="text-red-700">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="e.g. Infinite Stratos, Inc." {...field} />
                    </FormControl>
                    <FormMessage className="text-red-700 text-xs" />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="einNumber"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel className="text-foreground">
                      EIN Number <span className="text-red-700">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="XX-XXXXXXX"
                        maxLength={10}
                        {...field}
                        onChange={(e) => {
                          const formattedValue = formatEIN(e.target.value);
                          field.onChange(formattedValue);
                        }}
                      />
                    </FormControl>
                    <FormMessage className="text-red-700 text-xs" />
                  </FormItem>
                )}
              />
            </div>

            <div className="md:grid-cols-2 grid-cols-1 grid gap-6">
              <FormField
                control={form.control}
                name="address1"
                render={({ field }) => (
                  <FormItem className={`space-y-1 w-full`}>
                    <FormLabel className="text-foreground">Address</FormLabel>
                    <FormControl>
                      <Autocomplete
                        apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
                        onPlaceSelected={handlePlaceSelect}
                        options={{
                          types: ['address'],
                          componentRestrictions: { country: 'us' }
                        }}
                        className="flex h-10 w-full rounded-lg !border-[hsl(var(--border))] bg-[hsl(var(--input))] px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 focus-visible:ring-black"
                        placeholder="Enter your address"
                        onKeyDown={handleKeyDown}
                        value={field.value}
                        style={{
                          border: '1px solid hsl(var(--border))',
                          backgroundColor: 'hsl(var(--input))'
                        }}
                        onChange={(e) => {
                          field.onChange((e.target as HTMLInputElement).value);
                        }}
                      />
                    </FormControl>
                    <FormMessage className="text-red-700 text-xs" />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="address2"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel className="text-foreground">Unit, PO Box, Suite etc</FormLabel>
                    <FormControl>
                      <Input placeholder="Optional" {...field} />
                    </FormControl>
                    <FormMessage className="text-red-700 text-xs" />
                  </FormItem>
                )}
              />
            </div>

            <div className="md:grid-cols-2 grid-cols-1 grid gap-6">
              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel className="text-foreground">
                      City <span className="text-red-700">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="e.g. New York City" {...field} />
                    </FormControl>
                    <FormMessage className="text-red-700 text-xs" />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="state"
                render={({ field }) => (
                  <FormItem className={`space-y-1`}>
                    <FormLabel className="text-foreground">
                      State <span className="text-red-700">*</span>
                    </FormLabel>
                    <FormControl>
                      <MultiSelector
                        className="bg-[hsl(var(--input))] border-[hsl(var(--border))]"
                        value={field.value}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage className="text-red-700 text-xs" />
                  </FormItem>
                )}
              />
            </div>

            <div className="md:grid-cols-2 grid-cols-1 grid gap-6">
              <FormField
                control={form.control}
                name="zip"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel className="text-foreground">
                      Zip Code <span className="text-red-700">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="e.g. 10001" {...field} />
                    </FormControl>
                    <FormMessage className="text-red-700 text-xs" />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="country"
                render={({ field }) => (
                  <FormItem className={`space-y-1`}>
                    <FormLabel className="text-foreground">
                      Country <span className="text-red-700">*</span>
                    </FormLabel>
                    <FormControl>
                      <MultiSelector
                        className="bg-[hsl(var(--input))] border-[hsl(var(--border))]"
                        value={field.value}
                        onChange={field.onChange}
                        items={COUNTRIES}
                      />
                    </FormControl>
                    <FormMessage className="text-red-700 text-xs" />
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
        </Card>

        <CardTitle className="text-lg pt-4 pd-4">Other Information</CardTitle>
        <Card>
          <CardContent className="pt-4">
            <div className="md:grid-cols-2 grid-cols-1 grid gap-6">
              <FormField
                control={form.control}
                name="timezone"
                render={({ field }) => (
                  <FormItem className={`space-y-1`}>
                    <FormLabel className="text-foreground">
                      Time Zone <span className="text-red-700">*</span>
                    </FormLabel>
                    <FormControl>
                      <MultiSelector
                        className="bg-[hsl(var(--input))] border-[hsl(var(--border))]"
                        value={field.value}
                        onChange={field.onChange}
                        items={TIMEZONES.map((timezone) => ({
                          value: timezone.name,
                          label: formatTimezone(timezone)
                        }))}
                      />
                    </FormControl>
                    <FormMessage className="text-red-700 text-xs" />
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-start w-full">
          <Button type="submit" disabled={loading || !isDirty} className="mt-2">
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </form>
    </Form>
  );
}
